import { TFunction } from "i18next";
import { PageBlockDto } from "~/modules/pageBlocks/dtos/PageBlockDto";
import { defaultFooter } from "../defaultFooter";
import { defaultHeader } from "../defaultHeader";
import Constants from "~/application/Constants";
import { defaultTestimonials } from "../defaultTestimonials";
import { defaultGallery } from "../defaultGallery";
import { defaultFaq } from "../defaultFaq";

export function defaultLandingPage({ t }: { t: TFunction }) {
  const blocks: PageBlockDto[] = [
    // Banner
    {
      banner: {
        style: "top",
        text: "In uncertain markets, staying ahead of your books is more critical than ever. Lunova helps founders avoid financial surprises — before they happen.",
        textMd: "",
        cta: [
          {
            text: "Try!",
            href: "/register",
            isPrimary: true,
            target: "_blank",
          },
        ],
      },
    },
    // Header
    { header: defaultHeader({ t }) },
    // Hero
    {
      hero: {
        style: "rightImage",
        headline: t("front.hero.headline1"),
        description: t("front.hero.headline2"),
        secondaryBadgeUrl: `${process.env.SUPABASE_API_URL}/storage/v1/object/public/public-image//quickbook%20badge.png`,
        image: `${process.env.SUPABASE_API_URL}/storage/v1/object/public/public-image//lunova%20hero%20landing%20page.png`,
        cta: [
          {
            text: t("front.hero.buy"),
            href: "/pricing",
            isPrimary: true,
          },
          {
            text: t("front.hero.contact"),
            href: "/contact",
            isPrimary: false,
          },
        ],
        topText: {
          text: t("front.hero.subheadline1"),
          // link: { text: t("front.hero.changelog"), href: "/changelog" },
        }
        // bottomText: {
        //   link: {
        //     text: t("front.hero.hint", { 0: `v${Constants.VERSION}` }),
        //     href: "/changelog",
        //   },
        // },
      },
    },
    {
      video: {
        style: "full",
        headline: "Watch a 2-Min Demo",
        src: "https://www.loom.com/embed/f4315d430e3541149aba200ceda4dc2d?sid=648ef383-8c23-474e-ba85-9bb6131bb607",
      },
    },
    {
      features: {
        style: "list",
        topText:
          "BUILT FOR FOUNDERS WHO WANT FINANCIAL VISIBILITY AND PROTECTION — ESPECIALLY WHEN EVERY DOLLAR COUNTS.",
        headline: "Features",
        cta: [
          { text: "Pricing", isPrimary: true, href: "/pricing" },
          { text: "Contact", isPrimary: false, href: "/contact" },
        ],
        grid: { columns: "5", gap: "sm" },
        items: [
          { name: "Know when invoices are overdue before they cause cash flow gaps." },
          { name: "Get notified instantly when customer payments hit your bank account." },
          { name: "Track your account balances and spot missing deposits or reconciliation issues." },
          {
            name: "Fully integrated with QuickBooks Online. No spreadsheets, no manual exports.",
          },
          { name: "Catch vendor price increases or unexpected bills early." },
        ],
      },
    },
    {
      features: {
        style: "cards",
        headline: "Supported QuickBooks Entities",
        subheadline: "We are adding more every couple of weeks!",
        position: "center",
        grid: { columns: "3", gap: "sm" },
        items: [
          {
            name: "Deposits",
            description: "Monitor deposits, amounts, and payment methods",
            icon: `<svg … ></svg>`,
            link: {
              text: "Info",
              href: "https://uselunova.com/docs/articles/alert-type-deposit",
            },
          },
          {
            name: "Invoices",
            description: "Track invoice due dates and payment status",
            icon: `<svg … ></svg>`,
            link: {
              text: "Info",
              href: "https://uselunova.com/docs/articles/alert-type-invoice",
            },
          },
          {
            name: "Bills",
            description: "Monitor bill balances and payment schedules",
            icon: `<svg … ></svg>`,
            link: {
              text: "Info",
              href: "https://uselunova.com/docs/articles/alert-type-bill",
            },
          },
          {
            name: "Products",
            description: "Track product-related notifications",
            link: {
              text: "Info",
              href: "https://uselunova.com/docs/articles/alert-type-product",
            },
          },
          {
            name: "Accounts",
            description: "Monitor account balances and classifications",
            link: {
              text: "Info",
              href: "https://uselunova.com/docs/articles/alert-type-account",
            },
          },
          {
            name: "Payments",
            description: "Monitor large incoming cash & AR payments",
            link: {
              text: "Info",
              href: "https://uselunova.com/docs/articles/alert-type-payment",
            },
          },
        ],
      },
    },
    // Gallery
    {
      gallery: {
        style: "carousel",
        items: defaultGallery,
      },
    },
    {
      testimonials: {
        style: "simple",
        headline: "Read what other business owners are saying about Lunova",
        items: [
          {
            role: "Owner",
            company: "Harvest Home Goods",
            name: "Mara J.",
            personalWebsite: "",
            avatar: "",
            title: "A lifesaver for monitoring vendor bills and price changes",
            quote:
              "Before Lunova, I was manually checking vendor bills and hoping nothing slipped through. Now, I get alerts when costs jump or pricing changes — it's saved us from multiple overcharges already. It's like having a financial assistant who never sleeps.",
            quoteUrl: "",
            createdAt: "2025-02-14T09:25:00.000Z",
            stars: 5,
          },
          {
            role: "COO",
            company: "Peak Athletic Supply",
            name: "James L.",
            personalWebsite: "",
            avatar: "",
            title: "Keeps our team proactive, not reactive",
            quote:
              "Lunova helps us stay ahead of our numbers. Our sales team gets notified when inventory arrives or when pricing changes. We’re shipping faster and quoting more accurately — and I don’t need to chase down reports anymore.",
            quoteUrl: "",
            createdAt: "2025-02-28T16:40:00.000Z",
            stars: 5,
          },
          {
            role: "Founder & CFO",
            company: "Modern Brew Co.",
            name: "Sarah P.",
            personalWebsite: "",
            avatar: "",
            title: "Exactly what QuickBooks was missing",
            quote:
              "I always wished QuickBooks had better alerting — Lunova nails it. We track deposits across multiple merchant accounts and instantly get notified. It’s become part of our daily workflow, and I genuinely don’t know how we managed without it.",
            quoteUrl: "",
            createdAt: "2025-03-10T13:05:00.000Z",
            stars: 5,
          },
          {
            role: "Operations Manager",
            company: "Evergreen Landscaping",
            name: "Luis M.",
            personalWebsite: "",
            avatar: "",
            title: "No more spreadsheets for deposit tracking",
            quote:
              "We used to rely on spreadsheets to track if payments landed. Lunova now gives us instant alerts on merchant deposits — no more guessing or backtracking. It's been a huge time-saver for our lean ops team.",
            quoteUrl: "",
            createdAt: "2025-03-22T11:50:00.000Z",
            stars: 5,
          },
          {
            role: "Bookkeeper",
            company: "Shoreline Design Studio",
            name: "Tina R.",
            personalWebsite: "",
            avatar: "",
            title: "Alerts tailored to *exactly* what we need",
            quote:
              "I set up custom alerts for high-value bills, overdue invoices, and deposit patterns. It's granular, easy to use, and helps me stay on top of my clients' books without constant digging through reports.",
            quoteUrl: "",
            createdAt: "2025-04-09T08:10:00.000Z",
            stars: 5,
          },
        ],
      },
    },
    // Pricing
    {
      pricing: {
        style: "simple",
        headline: t("front.pricing.title"),
        subheadline: t("front.pricing.headline"),
        allowCoupons: true,
        contactUs: {
          title: "pricing.contactUs",
          description: "pricing.customPlanDescription",
          features: ["More users", "More alerts", "More support"],
        },
      },
    },
    {
      faq: {
        style: "simple",
        headline: "Frequently Asked Questions",
        items: [
          {
            question: "What is Lunova?",
            answer:
              "Lunova is a platform that allows you to create custom alerts for QuickBooks Online, automating financial monitoring by notifying you of key events such as deposits, bills, and invoices.",
          },
          {
            question: "How do I set up custom alerts?",
            answer:
              "After signing up, access the rule builder in your Lunova dashboard. Select an attribute (e.g., Total Amount, Payment Method) and configure the corresponding condition and value to trigger an alert.",
          },
          {
            question: "Will Lunova modify my QuickBooks data?",
            answer:
              "No, Lunova does not modify, update, delete, or otherwise alter any of your QuickBooks data. We only monitor your data and trigger alerts based on the data we receive from QuickBooks.",
          },
          {
            question: "Which QuickBooks entities does Lunova support?",
            answer:
              "Currently, Lunova supports key QuickBooks entities including deposits, bills, and invoices, with plans to expand to additional entities as we grow.",
          },
          {
            question: "How does Lunova integrate with QuickBooks?",
            answer:
              "Lunova integrates with QuickBooks Online using webhooks and API calls, enabling real-time data synchronization so that alerts are triggered based on the most current financial information.",
          },
          {
            question: "What types of alerts can I create?",
            answer:
              "You can create various alerts such as high-value deposit notifications, vendor bill increases, overdue invoices, and more. Each alert is fully customizable based on your specific business rules.",
          },
          {
            question: "What pricing plans are available?",
            answer:
              "We offer two tiers: Basic at $29/month (or $300/year) and Pro at $79/month (or $850/year), each designed to meet different usage needs.",
          },
          {
            question: "Is there a money-back guarantee?",
            answer:
              "Yes, we offer a 14-day money-back guarantee for all new users.",
          },
          {
            question: "What support options do you offer?",
            answer:
              "Basic users receive email support during business hours. Pro and Enterprise plans come with priority support via email and chat, with Enterprise customers enjoying dedicated account management.",
          },
          {
            question: "How do I connect my QuickBooks account?",
            answer:
              "Simply navigate to the settings in your Lunova dashboard and follow the steps to integrate your QuickBooks Online account. Detailed instructions are provided in our documentation.",
          },
          {
            question: "How does Lunova comply with GDPR?",
            answer:
              "Lunova is committed to GDPR compliance by ensuring data protection and privacy for all users. We implement robust security measures, provide data access controls, allow users to manage their personal information, and support data deletion requests. Additionally, we only process and store data necessary for our services and ensure third-party integrations meet GDPR standards.",
          },
        ],
      },
    },
    {
      blogPosts: {
        style: "simple",
        withCoverImage: true,
        withAuthorName: true,
        withAuthorAvatar: true,
        withDate: true,
        blogPath: "/blog",
        data: null,
      },
    },
    // Footer
    {
      footer: defaultFooter({ t }),
    },
  ];
  return blocks;
}
